import React, { useState } from 'react';
import { BuildUpPattern } from '@/services/api';

const formatAction = (action: string): string => action.replace(/_/g, ' ');

const PatternRow: React.FC<{ pattern: BuildUpPattern; index: number }> = ({ pattern, index }) => {
  const [expanded, setExpanded] = useState(false);
  const parts = pattern.pattern.split(' -> ');

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      <div
        className="p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 flex items-start justify-between gap-3"
        onClick={() => setExpanded(!expanded)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpanded(!expanded); } }}
        role="button"
        tabIndex={0}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs font-bold">
              {index + 1}
            </span>
            <span className="inline-block px-2 py-0.5 text-xs font-bold rounded bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300">
              {pattern.frequency}x
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              Avg {pattern.avgSequenceLen.toFixed(1)} events
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-1">
            {parts.map((part, idx) => (
              <React.Fragment key={`${part}-${idx}`}>
                {idx > 0 && (
                  <svg className="w-3 h-3 text-gray-400 dark:text-gray-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                )}
                <span className={`text-xs px-1.5 py-0.5 rounded ${
                  part.includes('goal')
                    ? 'bg-green-600 text-white font-medium'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                }`}>
                  {formatAction(part)}
                </span>
              </React.Fragment>
            ))}
          </div>
        </div>
        <svg className={`w-4 h-4 text-gray-400 transition-transform flex-shrink-0 mt-1 ${expanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>
      {expanded && (
        <div className="px-3 pb-3 border-t border-gray-200 dark:border-gray-700 pt-2">
          <div className="flex flex-wrap gap-2 mb-2">
            {pattern.commonStartZone && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400">
                Start: {pattern.commonStartZone.replace(/-/g, ' ')}
              </span>
            )}
            {pattern.commonFlank && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400">
                Flank: {pattern.commonFlank}
              </span>
            )}
          </div>
          <div className="space-y-1">
            {pattern.goals.map((goal) => (
              <div key={`${goal.matchId}-${goal.minute}`} className="text-xs text-gray-600 dark:text-gray-400 flex items-center gap-2">
                <span className="font-mono">{goal.minute}'</span>
                {goal.scorer && <span className="font-medium text-gray-800 dark:text-gray-200">{goal.scorer}</span>}
                <span className="text-gray-400 dark:text-gray-500 truncate">{goal.matchLabel}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PatternRow;
